import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import values from 'lodash/values';
import moment from 'moment';
import { DatePicker, SelectGroup } from 'components';
import MonthPickerV2 from 'components/MonthPicker/indexV2';
import './style.scss';

export const DateRangeType = {
  Monthly: 'monthly',
  Weekly: 'weekly',
  Biweekly: 'biweekly',
  Daily: 'daily',
  SemiAnnually: 'semiannually',
  Custom: 'range'
};

export const getDateRange = (date, type) => {
  if (!values(DateRangeType).includes(type)) return null;

  if (type === DateRangeType.SemiAnnually) {
    const m = moment(date);
    const year = m.year();
    const month = m.month();
    let start, end;

    end = moment([year, month, 1])
      .endOf('month')
      .toDate();
    start = moment([year, month, 1])
      .subtract(5, 'months')
      .startOf('month')
      .toDate();

    return [start, end];
  }

  const rangeType = type === DateRangeType.Monthly ? 'month' : type === DateRangeType.Daily ? 'day' : 'isoWeek';

  const endDate =
    type === DateRangeType.Biweekly
      ? moment(date)
          .add(1, 'week')
          .toDate()
      : date;

  return [
    moment(date)
      .startOf(rangeType)
      .toDate(),
    moment(endDate)
      .endOf(rangeType)
      .toDate()
  ];
};

class DateFilterV2 extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    disableFuture: PropTypes.bool,
    disableDaily: PropTypes.bool,
    disableWeekly: PropTypes.bool,
    disableBiweekly: PropTypes.bool,
    disableMonthly: PropTypes.bool,
    disableSemiAnnually: PropTypes.bool,
    displayDateRange: PropTypes.bool,
    onChange: PropTypes.func,
    hideSelectGroup: PropTypes.bool,
    hasCustom: PropTypes.bool
  };

  static defaultProps = {
    disabled: false,
    disableFuture: false,
    disableDaily: true,
    disableWeekly: false,
    disableBiweekly: false,
    disableMonthly: false,
    disableSemiAnnually: true,
    displayDateRange: false,
    hideSelectGroup: false,
    hasCustom: false
  };

  constructor(props) {
    super(props);
    const type = props.defaultValue?.type || DateRangeType.Monthly;
    const date = this.getDateFromProps();
    this.state = {
      type,
      date,
      dateRange: getDateRange(date, type)
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(this.getValue(prevState), this.getValue())) {
      this.handleChange();
    }
  }

  getDateFromProps() {
    const { defaultValue } = this.props;
    return defaultValue?.date || defaultValue?.dateRange?.[0] || new Date();
  }

  getValue(_state) {
    const state = _state || this.state;
    return {
      type: state.type,
      dateRange: state.dateRange.map(d => moment(d).format('YYYY-MM-DD'))
    };
  }

  @bind
  handleChange() {
    this.props.onChange?.(this.getValue());
  }

  @bind
  changeDateRange(dateRange) {
    this.setState({ dateRange: Array.isArray(dateRange) ? dateRange : [dateRange, dateRange] });
  }

  @bind
  changeMonthPicker(dateRange) {
    this.setState({ dateRange });
  }

  @bind
  changeType(type) {
    const date = this.state.date;
    this.setState({ type, dateRange: getDateRange(date, type) });
  }

  render() {
    const { type, date, dateRange } = this.state;
    const {
      disabled,
      disableFuture,
      disableDaily,
      disableWeekly,
      disableBiweekly,
      disableMonthly,
      disableSemiAnnually,
      displayDateRange,
      hideSelectGroup,
      hasCustom
    } = this.props;

    const items = [];
    if (!disableDaily) items.push(<SelectGroup.Item key="daily" value={DateRangeType.Daily} label="Daily" />);

    if (!disableWeekly) {
      items.push(<SelectGroup.Item key="weekly" value={DateRangeType.Weekly} label="Weekly" />);
    }
    if (!disableBiweekly) {
      items.push(<SelectGroup.Item key="biweekly" value={DateRangeType.Biweekly} label="Bi-Weekly" />);
    }
    if (!disableMonthly) items.push(<SelectGroup.Item key="monthly" value={DateRangeType.Monthly} label="Monthly" />);
    if (!disableSemiAnnually) {
      items.push(<SelectGroup.Item key="semiannually" value={DateRangeType.SemiAnnually} label="Semi-Annually" />);
    }
    if (hasCustom) items.push(<SelectGroup.Item key="range" value={DateRangeType.Custom} label="Custom" />);

    return (
      <div className="date-filter">
        {!hideSelectGroup && (
          <SelectGroup
            type="radio"
            className="date-filter__type"
            onChange={this.changeType}
            checked={type}
            disabled={disabled}
          >
            {items}
          </SelectGroup>
        )}
        <div className="date-filter__date">
          {type === DateRangeType.Monthly || type === DateRangeType.SemiAnnually ? (
            <MonthPickerV2
              data-cy="date-filter-month-picker"
              onChange={this.changeMonthPicker}
              defaultValue={type === DateRangeType.SemiAnnually ? dateRange : date}
              range={type === DateRangeType.SemiAnnually ? dateRange : undefined}
              disabled={disabled}
              disableFuture={disableFuture}
              displayDateRange={displayDateRange}
              ref={node => (this.monthPickerRef = node)}
            />
          ) : (
            <DatePicker
              data-cy="date-filter-date-picker"
              type={type}
              value={type === DateRangeType.Daily ? dateRange[0] : dateRange}
              onChange={this.changeDateRange}
              disabled={disabled}
              disableFuture={disableFuture}
            />
          )}
        </div>
      </div>
    );
  }
}

export default DateFilterV2;
